import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchParentReportDraft,
  saveParentReportDraft,
  type ParentReportDraft,
  type ParentReportDraftContentV1
} from "@/services/repositories/report-draft-repository";
import { useVersionedAutosave } from "@/hooks/useVersionedAutosave";

export function useParentReportDraft({
  assessmentId,
  defaultContent,
  defaultsReady
}: {
  assessmentId: string | undefined;
  defaultContent: ParentReportDraftContentV1;
  defaultsReady: boolean;
}) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState<ParentReportDraftContentV1>();
  const contentRef = useRef<ParentReportDraftContentV1>();
  const initializedAssessmentRef = useRef<string>();

  const draftQuery = useQuery({
    queryKey: ["parentReportDraft", assessmentId],
    enabled: Boolean(assessmentId),
    queryFn: () => fetchParentReportDraft(assessmentId!),
    staleTime: 30 * 1000
  });

  const {
    reset,
    queue,
    flush: flushAutosave,
    retry,
    status: saveStatus,
    error: saveError,
    version
  } = useVersionedAutosave<ParentReportDraftContentV1, ParentReportDraft>({
    initialVersion: 0,
    enabled: Boolean(assessmentId),
    save: (payload, expectedVersion) =>
      saveParentReportDraft(assessmentId!, payload, expectedVersion),
    onSaved: (saved) => {
      queryClient.setQueryData(["parentReportDraft", saved.assessmentId], { draft: saved });
    }
  });

  useEffect(() => {
    if (!assessmentId) {
      initializedAssessmentRef.current = undefined;
      contentRef.current = undefined;
      setContent(undefined);
      reset(0);
      return;
    }
    if (
      initializedAssessmentRef.current === assessmentId ||
      draftQuery.isLoading ||
      draftQuery.isError ||
      (!draftQuery.data?.draft && !defaultsReady)
    ) {
      return;
    }
    const initial = draftQuery.data?.draft?.content ?? defaultContent;
    initializedAssessmentRef.current = assessmentId;
    contentRef.current = initial;
    setContent(initial);
    reset(draftQuery.data?.draft?.version ?? 0);
  }, [
    assessmentId,
    defaultContent,
    defaultsReady,
    draftQuery.data,
    draftQuery.isError,
    draftQuery.isLoading,
    reset
  ]);

  const updateContent = useCallback(
    (
      update:
        | ParentReportDraftContentV1
        | ((previous: ParentReportDraftContentV1) => ParentReportDraftContentV1),
    ) => {
      const previous = contentRef.current;
      if (!previous) return;
      const next = typeof update === "function" ? update(previous) : update;
      contentRef.current = next;
      setContent(next);
      queue(next);
    },
    [queue],
  );

  const flush = useCallback(
    async ({ forceCreate = false }: { forceCreate?: boolean } = {}) => {
      const current = contentRef.current;
      if (!current) return false;
      return flushAutosave({ payload: current, force: forceCreate });
    },
    [flushAutosave],
  );

  const reload = useCallback(async () => {
    if (!assessmentId) return false;
    const result = await draftQuery.refetch();
    if (result.error) return false;
    const draft = result.data?.draft;
    const next = draft?.content ?? defaultContent;
    initializedAssessmentRef.current = assessmentId;
    contentRef.current = next;
    setContent(next);
    reset(draft?.version ?? 0);
    return true;
  }, [assessmentId, defaultContent, draftQuery, reset]);

  return {
    content,
    updateContent,
    flush,
    retry,
    reload,
    saveStatus,
    saveError,
    version,
    isLoading: draftQuery.isLoading || (Boolean(assessmentId) && !content),
    loadError: draftQuery.error
  };
}
